import useAppStore from "@/context/store";
import {
  createCollections,
  deleteCollection,
  getCollections,
  moveCollectionNextTo,
  updateCollection,
} from "@/services";
import useRequest from "@/utils/useReq";
import { useMemoizedFn, useMount, useSafeState } from "ahooks";
import { Button } from "antd";
import classNames from "classnames";
import { pick, truncate } from "lodash";
import { useRef, useState } from "react";
import MutiSelect from "../BoardSelect/MutiSelect";
import HBIcon from "../HBIcon";
import HBInput from "../HBInput";
import HBMessage from "../HBMessage";
import HBModal from "../HBModal";
import ModalWrap from "../HBModal/ModalWrap";
import HBConfirm from "../HBModal/confirm";
import { SortableList } from "../SortableList";
import styles from "./index.module.less";

function CollectionManagerDialogRaw({ targetId, create, onCancel, ...props }) {
  const [current, setCurrent] = useState({});
  const listRef = useRef();
  const mutiSelectRef = useRef();
  const [isDraging, setDraging] = useState(false);
  const [createClass, setCreateClass] = useSafeState();
  const {
    data: collections,
    mutate: setCollections,
    runAsync: refreshCollections,
  } = useRequest(
    async (init) => {
      const { collections, err } = await getCollections(
        useAppStore.getState().user?.user_id
      );
      if (err) return [];
      if (init) {
        if (targetId) {
          setCurrent(
            collections.find((item) => item.collection_id == targetId)
          );
          setTimeout(() => {
            if (listRef.current) {
              const index = collections.findIndex(
                (item) => item.collection_id == targetId
              );
              listRef.current?.scrollTo({
                top: index * 50,
                behavior: "smooth",
              });
            }
          }, 200);
        } else {
          if (!create) {
            setCurrent(collections[0]);
          }
        }
      }
      return collections || [];
    },
    {
      manual: true,
    }
  );

  const [validateErr, setErr] = useState([]);
  const validate = useMemoizedFn((value) => {
    const validateErr = [];
    if (!value.title) {
      validateErr.push({ field: "title", message: "请填写专辑名" });
    }
    if (value.title.length > 32) {
      validateErr.push({ field: "title", message: "专辑名不能超过32个字符" });
    }

    setErr(validateErr);
    return validateErr;
  });

  const onFormChange = useMemoizedFn((allValues) => {
    validate(allValues);
    setCurrent(allValues);
  });

  const onDelete = useMemoizedFn(() => {
    HBConfirm.open({
      title: "删除专辑",
      content: `确定删除专辑【${current.title}】？专辑内的画板不会被删除`,
      okText: "确定",
      cancelText: "取消",
      onOk: async () => {
        if (current.collection_id) {
          const { err } = await deleteCollection({
            collection_id: current.collection_id,
          });

          if (err) return;
        }
        HBMessage.success("删除成功");

        const nextCollections = collections.filter(
          (item) => item.collection_id !== current.collection_id
        );
        // update list
        setCollections(nextCollections);
        setCurrent(nextCollections[0]);
        mutiSelectRef.current?.refresh();
        listRef.current?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      },
    });
  });

  const animotionRef = useRef();
  // 创建动画
  const createAnimotion = useMemoizedFn(() => {
    if (animotionRef.current) {
      clearTimeout(animotionRef.current);
      // 恢复默认状态
      setCreateClass("");
    }
    // 动画初始状态（上移50px， 第一个透明）
    setCreateClass(styles.beforeCreate);

    animotionRef.current = setTimeout(() => {
      // 动画状态( 下移50px 颜色渐变 透明渐变)
      setCreateClass(styles.afterCreate);

      // 恢复默认状态
      animotionRef.current = setTimeout(() => {
        setCreateClass("");
      }, 800);
    }, 200);
  });

  const onCreate = useMemoizedFn(async () => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: 0, behavior: "smooth" });
      document.getElementById("collectionInput").focus();
    }

    const length = collections.filter((item) =>
      item.title.startsWith("未命名专辑")
    ).length;

    const { collection, err } = await createCollections(
      "未命名专辑" + (length ? `(${length})` : "")
    );

    if (err) return;
    setCollections([collection, ...(collections || [])]);
    onSwitch(collection);

    createAnimotion();
  });

  useMount(async () => {
    await refreshCollections(true);
    if (create) {
      onCreate();
    }
  });

  const onSortEnd = useMemoizedFn(async (oldIndex, newIndex) => {
    setDraging(false);
    if (oldIndex === newIndex) return;
    const oldList = [...collections];
    collections.splice(newIndex, 0, collections.splice(oldIndex, 1)[0]);
    setCollections([...collections]);

    const { err } = await moveCollectionNextTo({
      currentId: oldList[oldIndex].collection_id,
      nextToId:
        oldList[oldIndex > newIndex ? newIndex - 1 : newIndex]?.collection_id,
    });
    if (err) {
      setCollections(oldList);
    }
  });

  const [saveLoading, setSaveLoading] = useState(false);
  const onSave = useMemoizedFn(async () => {
    const validateInfo = validate(current);
    if (validateInfo.length) return;
    setSaveLoading(true);

    const { err, msg } = await updateCollection(
      pick(current, ["title", "collection_id", "board_ids"])
    );
    if (err) return;
    HBMessage.success({
      content: msg || "保存成功",
      duration: 2,
      key: "collectionManager",
    });
    setCollections(
      collections.map((item) => {
        if (item.collection_id === current.collection_id) {
          return current;
        } else {
          return item;
        }
      })
    );
    setSaveLoading(false);
    refreshCollections();
    mutiSelectRef.current?.refresh();
  });

  const checkChange = useMemoizedFn(() => {
    return new Promise((resolve) => {
      if (collections.length === 0) {
        resolve();
      }

      const old =
        collections.find(
          (record) => record.collection_id === current?.collection_id
        ) || {};

      if (current.title === old.title && current.board_ids === old.board_ids) {
        resolve();
      } else {
        HBConfirm.open({
          title: "保存提示",
          content: "修改的内容还没保存，是否保存？",
          async onOk() {
            await onSave();
            resolve();
          },
          okText: "保存",
          cancelText: "不保存",
          hideIcon: true,
          onCancel() {
            resolve();
          },
        });
      }
    });
  });

  const onSwitch = useMemoizedFn(async (item) => {
    if (!current) {
      setCurrent(item);
      return;
    }
    return checkChange().then(() => {
      setCurrent(item);
    });
  }, []);

  const boardDataMap = useMemoizedFn((board) => {
    const boardCollectionId = board.extra?.collection_id;
    board.disabled =
      boardCollectionId != current.collection_id &&
      boardCollectionId !== undefined &&
      collections.find((item) => item.collection_id == boardCollectionId);

    if (board.disabled) {
      board.disableText = `已关联专辑“${truncate(
        collections.find((item) => item.collection_id == boardCollectionId)
          ?.title,
        { length: 7 }
      )}”`;
    }
    return board;
  });

  const onCancelWrap = useMemoizedFn(async () => {
    return checkChange().then(() => {
      onCancel();
    });
  });

  return (
    <HBModal
      {...props}
      footer={null}
      title={null}
      width={850}
      bodyStyle={{ padding: 0 }}
      onCancel={onCancelWrap}
      forceRender
    >
      <div
        className={classNames(
          { [styles.draging]: isDraging },
          styles.container
        )}
      >
        {collections?.length ? (
          <>
            <div className={styles.left}>
              <div className={styles.title}>管理专辑</div>

              <div onClick={onCreate} className={styles.add}>
                <span className={styles.btn}>
                  <HBIcon type="plus" />
                  创建专辑
                </span>
              </div>
              <div
                className={classNames("thin-scroll-bar", styles.list)}
                ref={listRef}
              >
                <SortableList
                  items={collections}
                  getItemId={(item) => item.collection_id}
                  onSortEnd={onSortEnd}
                  onSortStart={() => setDraging(true)}
                  renderItem={({
                    item,
                    isDragging: itemIsDragging,
                    ref,
                    props,
                    handleProps,
                  }) => {
                    return (
                      <div
                        className={classNames(
                          styles.collectionItem,
                          createClass,
                          {
                            [styles.active]:
                              current?.collection_id === item.collection_id,
                            [styles.isDragging]: itemIsDragging,
                          }
                        )}
                        key={item.collection_id}
                        title={item.title}
                        ref={ref}
                        {...(isDraging ? props : null)}
                        {...handleProps}
                        onClick={() => onSwitch(item)}
                      >
                        <span className={styles.text}>{item.title}</span>
                        <HBIcon type="drag_indicator" />
                      </div>
                    );
                  }}
                />
              </div>
            </div>

            <div className={styles.right}>
              {current && (
                <>
                  <div className={styles.title}>编辑专辑</div>

                  <div className={styles.form}>
                    <CollectionForm
                      value={current}
                      onChange={onFormChange}
                      validateErr={validateErr}
                      boardDataMap={boardDataMap}
                      mutiSelectRef={mutiSelectRef}
                    />
                  </div>

                  <div className={styles.footer}>
                    <a className={"primary-text-btn"} onClick={onDelete}>
                      删除专辑
                    </a>
                    <Button
                      onClick={onSave}
                      type="primary"
                      style={{ width: 88 }}
                      loadding={saveLoading}
                    >
                      保存
                    </Button>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <div className={styles.empty}>
            <div className={styles.title}>管理专辑</div>
            <div className={styles.img}>
              <img
                src="//cdn.dancf.com/fe-assets/img/51955b0159931b2955a8fa20d384da75.png"
                alt="空图标"
                width={110}
              />
            </div>
            <div className={styles.desc}>还没有专辑</div>
            <div onClick={onCreate} className={styles.add}>
              <span className={styles.btn}>
                <HBIcon type="plus" />
                创建专辑
              </span>
            </div>
          </div>
        )}
      </div>
    </HBModal>
  );
}

const CollectionManagerDialog = ModalWrap(CollectionManagerDialogRaw);
export default CollectionManagerDialog;

function CollectionForm({
  value,
  onChange,
  validateErr = [],
  boardDataMap,
  mutiSelectRef,
}) {
  const onClear = useMemoizedFn(() => {
    onChange({ ...value, board_ids: "" });
  }, []);

  const labelRender = useMemoizedFn((value) => (
    <div className={styles.label}>
      {`关联画板(${value?.length || 0})`}
      {value?.length > 0 && (
        <div
          className={classNames(styles.clearBtn, "primary-text-btn")}
          onClick={onClear}
        >
          清空
        </div>
      )}
    </div>
  ));

  return (
    <>
      <div className={classNames(styles.label, styles.required)}>专辑名</div>
      <HBInput
        id="collectionInput"
        noWrapper
        placeholder="请填写专辑名"
        className={classNames(styles.input)}
        wrapClass={classNames({
          [styles.errInput]: validateErr.find((item) => item.field === "title")
            ?.message,
        })}
        value={value.title}
        autoFocus
        onChange={(v) => {
          onChange({ ...value, title: v.target.value });
        }}
      />
      <div className={styles.err}>
        {validateErr.find((item) => item.field === "title")?.message}
      </div>

      <MutiSelect
        placeholder="添加画板（ 只能选还没有关联专辑的画板）"
        value={value?.board_ids}
        onChange={(v) => onChange({ ...value, board_ids: v })}
        useCreation
        boardDataMap={boardDataMap}
        labelRender={labelRender}
        onlyUsersBoards
        {...(mutiSelectRef ? { ref: mutiSelectRef } : {})}
      />
    </>
  );
}
